import React, { useState } from 'react';
import type { HorseProfile } from '../../../types/horseProfile';
import { BasicInfoSection } from './sections/BasicInfoSection';
import { HealthSection } from './sections/HealthSection';
import { SocialBehaviorSection } from './sections/SocialBehaviorSection';
import { CareSection } from './sections/CareSection';
import { PerformanceSection } from './sections/PerformanceSection';
import { EmergencySection } from './sections/EmergencySection';

interface HorseProfileViewProps {
  horse: HorseProfile;
  onEdit?: (section: keyof HorseProfile) => void;
}

const SECTIONS = [
  { id: 'health', label: 'Gesundheit' },
  { id: 'socialBehavior', label: 'Verhalten & Soziales' },
  { id: 'care', label: 'Pflege' },
  { id: 'performance', label: 'Leistung & Training' },
  { id: 'emergency', label: 'Notfall' }
] as const;

export const HorseProfileView: React.FC<HorseProfileViewProps> = ({ horse, onEdit }) => {
  const [activeSection, setActiveSection] = useState<string>(SECTIONS[0].id);
  const [isMobile] = useState(window.innerWidth < 640);

  const renderContent = () => {
    const canView = true; // For now, allow viewing all sections

    let content;
    switch (activeSection) {
      case 'health':
        content = <HealthSection health={horse.health} />;
        break;
      case 'socialBehavior':
        content = <SocialBehaviorSection behavior={horse.socialBehavior} />;
        break;
      case 'care':
        content = <CareSection care={horse.care} />;
        break;
      case 'performance':
        content = <PerformanceSection performance={horse.performance} />;
        break;
      case 'emergency':
        content = <EmergencySection emergency={horse.emergency} />;
        break;
      default:
        return null;
    }

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {SECTIONS.find(s => s.id === activeSection)?.label}
          </h2>
          {onEdit && (
            <button
              onClick={() => onEdit(activeSection as keyof HorseProfile)}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 transition-colors"
            >
              Bearbeiten
            </button>
          )}
        </div>
        {content}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 dark:border-gray-700">
        {isMobile ? (
          <div className="px-4 py-3">
            <select
              value={activeSection}
              onChange={(e) => setActiveSection(e.target.value)}
              className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400"
            >
              {SECTIONS.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {SECTIONS.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${activeSection === section.id
                    ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
                  }
                  transition-colors duration-200
                `}
              >
                {section.label}
              </button>
            ))}
          </nav>
        )}
      </div>

      {renderContent()}
    </div>
  );
};